import React from 'react';
import { TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, FormGroup, FormControlLabel, Checkbox, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import styled from 'styled-components';
import { User } from '../../../../../../apis/users';
import { PrescriberFormData, prescriberSchema, emptyProtocol } from './types';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ConditionSelector from '../../../../../ConditionSelector';
import InfoTooltip from '../../../../../InfoTooltip';
import ImageEditor from '../../../../../FormEditors/ImageEditor';

const AVAILABLE_ROLES = ['pharmacist', 'prescriber', 'patient', 'account_manager'];

const PROTOCOLS_HELP =
  'Pick the condition the policy covers and give the address of the policy document (must be a publicly reachable https:// link). When this prescriber claims a prescription request for that condition, the "Prescribing Protocol" link on the Connect order opens their document instead of Healthera\'s. Conditions not listed here keep the condition\'s default protocol.';

interface PrescriberFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PrescriberFormData) => Promise<void>;
  selectedUser: User | null;
  dialogMode: 'add_new' | 'add_existing' | 'edit';
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearchKeyPress: (event: React.KeyboardEvent) => void;
  isSearching: boolean;
  searchQuery: string;
  searchResults?: { data: User[] };
  onUserSelect: (user: User) => void;
  initialFormData: PrescriberFormData;
}

const getRoleLabel = (role: string) => {
  switch (role) {
    case 'account_manager':
      return 'Account Manager';
    case 'pharmacist':
      return 'Pharmacist';
    case 'prescriber':
      return 'Prescriber';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
};

const PrescriberFormDialog: React.FC<PrescriberFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  dialogMode,
  searchInput,
  onSearchInputChange,
  onSearchKeyPress,
  searchResults,
  onUserSelect,
  initialFormData
}) => {
  const {
    control,
    handleSubmit: handleRHFSubmit,
    formState: { errors },
    reset,
  } = useForm<PrescriberFormData>({
    resolver: yupResolver(prescriberSchema),
    defaultValues: initialFormData,
    shouldFocusError: false,
  });

  const { fields: protocolFields, append: appendProtocol, remove: removeProtocol } = useFieldArray({
    control,
    name: 'prescribing_protocols',
  });

  React.useEffect(() => {
    reset(initialFormData);
  }, [initialFormData, reset]);

  const onFormSubmit = (data: PrescriberFormData) => {
    console.log('RHF validated data:', data);
    onSubmit(data);
  };

  const getDialogTitle = () => {
    if (dialogMode === 'edit') return 'Edit Prescriber';
    if (dialogMode === 'add_new') return 'Add New Prescriber';
    return 'Add Existing User as Prescriber';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {getDialogTitle()}
        <Typography variant="caption" style={{ marginLeft: '8px', color: '#666' }}>
          This information will be included in the prescription
        </Typography>
      </DialogTitle>
      <DialogContent>
        {dialogMode === 'add_existing' && (
          <SearchContainer>
            <TextField
              fullWidth
              label="Search by full email address"
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              onKeyPress={onSearchKeyPress}
            />
            {searchResults?.data && (
              <SearchResultsContainer>
                {searchResults.data.map(user => (
                  <SearchResultItem
                    key={user._id}
                    onClick={() => onUserSelect(user)}
                  >
                    <Typography>
                      {user.forename} {user.surname} ({user.username})
                    </Typography>
                  </SearchResultItem>
                ))}
              </SearchResultsContainer>
            )}
          </SearchContainer>
        )}
        <FormContainer>
          <form onSubmit={handleRHFSubmit(onFormSubmit)} noValidate>
            <Grid container>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller
                  name="forename"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="First Name"
                      error={!!errors.forename}
                      helperText={errors.forename?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller
                  name="surname"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Last Name"
                      error={!!errors.surname}
                      helperText={errors.surname?.message}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={dialogMode !== 'add_new'}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="nmcNumber" control={control} render={({ field }) => <TextField {...field} fullWidth label="NMC Number" error={!!errors.nmcNumber} helperText={errors.nmcNumber?.message} />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="gphcNumber" control={control} render={({ field }) => <TextField {...field} fullWidth label="GPhC Number" error={!!errors.gphcNumber} helperText={errors.gphcNumber?.message} />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="registration_body" control={control} render={({ field }) => <TextField {...field} fullWidth label="Registration Body" error={!!errors.registration_body} helperText={errors.registration_body?.message} />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="address" control={control} render={({ field }) => <TextField {...field} fullWidth label="Address" error={!!errors.address} helperText={errors.address?.message} />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="postcode" control={control} render={({ field }) => <TextField {...field} fullWidth label="Postcode" error={!!errors.postcode} helperText={errors.postcode?.message} />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="telephone" control={control} render={({ field }) => <TextField {...field} fullWidth label="Telephone" error={!!errors.telephone} helperText={errors.telephone?.message} />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="clinic_name" control={control} render={({ field }) => <TextField {...field} fullWidth label="Clinic Name" error={!!errors.clinic_name} helperText={errors.clinic_name?.message} />} />
              </Grid>
              <Grid item xs={8} style={{ padding: '6px' }}>
                <Typography variant="subtitle1">Roles *</Typography>
                <Controller
                  name="roles"
                  control={control}
                  render={({ field }) => (
                    <HorizontalFormGroup>
                      {AVAILABLE_ROLES.map(role => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox
                              checked={field.value.includes(role)}
                              onChange={(e) => {
                                const updatedRoles = e.target.checked
                                  ? [...field.value, role]
                                  : field.value.filter((r: string) => r !== role);
                                field.onChange(updatedRoles);
                              }}
                              name={role}
                            />
                          }
                          label={getRoleLabel(role)}
                        />
                      ))}
                    </HorizontalFormGroup>
                  )}
                />
                {errors.roles && <Typography color="error" variant="caption">{errors.roles.message}</Typography>}
              </Grid>
              <Grid item xs={12} style={{ padding: '6px' }}>
                <Typography variant="subtitle1">Prescribing Protocols</Typography>
                <Typography variant="caption" style={{ color: '#666' }}>
                  The prescriber's own prescribing policy per condition. Conditions left unset here fall back to the condition's default protocol.
                </Typography>
                {protocolFields.map((protocolField, index) => (
                  <ProtocolRow key={protocolField.id}>
                    <Grid container>
                      <Grid item xs={4} style={{ padding: '8px' }}>
                        <Controller
                          name={`prescribing_protocols.${index}.condition_id`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <>
                              <Typography variant="caption">Condition</Typography>
                              <ConditionSelector value={field.value} onChange={field.onChange} />
                              {fieldState.error && (
                                <Typography color="error" variant="caption">{fieldState.error.message}</Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item xs={7} style={{ padding: '8px' }}>
                        <Controller
                          name={`prescribing_protocols.${index}.url`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Protocol URL"
                              placeholder="https://yourclinic.co.uk/prescribing-policy"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={1} style={{ padding: '8px', display: 'flex', alignItems: 'center' }}>
                        <IconButton type="button" aria-label="Remove protocol" onClick={() => removeProtocol(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </ProtocolRow>
                ))}
                <Button
                  type="button"
                  variant="outlined"
                  style={{ color: '#2F9D6D', borderColor: '#2F9D6D', marginTop: '8px' }}
                  onClick={() => appendProtocol({ ...emptyProtocol }, { shouldFocus: false })}
                >
                  <AddIcon style={{ marginRight: 8 }} />
                  Add Protocol
                </Button>
                <InfoTooltip title={PROTOCOLS_HELP} />
              </Grid>
              <Grid item xs={12} style={{ padding: '6px', marginTop: '16px' }}>
                <Typography variant="subtitle1">Clinic page profile</Typography>
                <Typography variant="caption" style={{ color: '#666' }}>
                  Shown with the prescriber's name and registration in the clinic page's prescriber section when "Show on this clinic's pages" is ticked, and next to any clinical review they sign.
                </Typography>
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <Controller name="public_profile.title" control={control} render={({ field }) => <TextField {...field} fullWidth label="Title" placeholder="Independent Prescriber" />} />
              </Grid>
              <Grid item xs={8} style={{ padding: '6px' }}>
                <Controller name="public_profile.bio" control={control} render={({ field }) => <TextField {...field} fullWidth label="One-line bio" placeholder="20+ years experience Pharmacist Prescriber" />} />
              </Grid>
              <Grid item xs={4} style={{ padding: '6px' }}>
                <ImageEditor control={control} name="public_profile.image_url" label="Picture" width="160px" height="160px" suggestedSize="400x400" />
              </Grid>
              <Grid item xs={8} style={{ padding: '6px' }}>
                <Controller
                  name="show_on_clinic"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} name="show_on_clinic" />}
                      label="Show on this clinic's pages"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </FormContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{dialogMode === 'edit' ? 'Close' : 'Cancel'}</Button>
        <Button
          onClick={handleRHFSubmit(onFormSubmit)}
          variant="contained"
          style={{ backgroundColor: '#2F9D6D', color: 'white' }}
        >
          {dialogMode === 'edit' ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrescriberFormDialog;

const HorizontalFormGroup = styled(FormGroup)`
  flex-direction: row !important;
  gap: 16px;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const SearchResultsContainer = styled.div`
  margin-top: 16px;
  max-height: 200px;
  overflow-y: auto;
`;

const SearchResultItem = styled.div`
  padding: 8px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const FormContainer = styled.div`
  margin-top: 24px;
`;

const ProtocolRow = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-top: 8px;
`;
